
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUserManagement } from '../hooks/useUserManagement';
import { useInfractions } from '../hooks/useInfractions';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Search, Users, Shield, AlertTriangle } from 'lucide-react';
import { Skeleton } from '../components/ui/skeleton';
import UserManagementTable from '../components/UserManagement/UserManagementTable';
import UserEditModal from '../components/UserManagement/UserEditModal';
import UserDeleteConfirmDialog from '../components/UserManagement/UserDeleteConfirmDialog';
import SuspendAccountModal from '../components/UserManagement/SuspendAccountModal';
import BanAccountModal from '../components/UserManagement/BanAccountModal';
import InfractionModal from '../components/UserManagement/InfractionModal';
import InfractionHistory from '../components/UserManagement/InfractionHistory';
import UserPostsViewer from '../components/UserManagement/UserPostsViewer';
import { SendPrivateNotificationModal } from '../components/AdminNotifications/SendPrivateNotificationModal';
import { SendGlobalNotificationModal } from '../components/AdminNotifications/SendGlobalNotificationModal';
import { BroadcastHistoryTable } from '../components/AdminNotifications/BroadcastHistoryTable';
import { AdminUser } from '../services/userManagementService';
import { InfractionSeverity } from '../services/infractionService';
import BottomTabNavigation from '../components/navigation/BottomTabNavigation';
import AdminDashboard from '../components/AdminDashboard';

const UserManagementPage = () => {
  const { t } = useTranslation();
  const {
    users,
    isLoading,
    isAdmin,
    searchTerm,
    setSearchTerm,
    updateUser,
    deleteUser,
    suspendUser,
    banUser,
    reactivateUser,
    isUpdating,
    isDeleting,
    isSuspending,
    isBanning,
    isReactivating,
  } = useUserManagement();

  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSuspendModalOpen, setIsSuspendModalOpen] = useState(false);
  const [isBanModalOpen, setIsBanModalOpen] = useState(false);
  const [isInfractionModalOpen, setIsInfractionModalOpen] = useState(false);
  const [isHistoryModalOpen, setIsHistoryModalOpen] = useState(false);
  const [isPostsViewerOpen, setIsPostsViewerOpen] = useState(false);
  const [isPrivateNotificationModalOpen, setIsPrivateNotificationModalOpen] = useState(false);
  const [isGlobalNotificationModalOpen, setIsGlobalNotificationModalOpen] = useState(false);

  const { addInfraction, isAdding, infractions, isLoading: isLoadingInfractions } = useInfractions(selectedUser?.id);

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 container mx-auto max-w-4xl p-4">
          <Card>
            <CardContent className="flex items-center justify-center py-12">
              <div className="text-center">
                <Shield className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('adminPanel.accessDenied')}</h3>
                <p className="text-muted-foreground">
                  {t('adminPanel.onlyAdminsCanAccess')}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  const handleEditUser = (user: AdminUser) => {
    setSelectedUser(user);
    setIsEditModalOpen(true);
  };

  const handleDeleteUser = (user: AdminUser) => {
    setSelectedUser(user);
    setIsDeleteDialogOpen(true);
  };

  const handleSuspendUser = (user: AdminUser) => {
    setSelectedUser(user);
    setIsSuspendModalOpen(true);
  };

  const handleBanUser = (user: AdminUser) => {
    setSelectedUser(user);
    setIsBanModalOpen(true);
  };

  const handleReactivateUser = (user: AdminUser) => {
    if (confirm(t('adminPanel.confirmReactivate', { name: user.full_name }))) {
      reactivateUser(user.id);
    }
  };

  const handleSaveUser = (userId: string, updates: any) => {
    updateUser({ userId, updates });
  };

  const handleConfirmDelete = (userId: string) => {
    deleteUser(userId);
  };

  const handleConfirmSuspend = (userId: string, reason: string, suspendUntil?: Date) => {
    suspendUser({ userId, reason, suspendUntil });
    setIsSuspendModalOpen(false);
  };

  const handleConfirmBan = (userId: string, reason: string) => {
    banUser({ userId, reason });
    setIsBanModalOpen(false);
  };

  const handleAddInfraction = (user: AdminUser) => {
    setSelectedUser(user);
    setIsInfractionModalOpen(true);
  };

  const handleViewHistory = (user: AdminUser) => {
    setSelectedUser(user);
    setIsHistoryModalOpen(true);
  };

  const handleViewPosts = (user: AdminUser) => {
    setSelectedUser(user);
    setIsPostsViewerOpen(true);
  };

  const handleConfirmInfraction = (userId: string, type: string, severity: InfractionSeverity, description: string) => {
    addInfraction({
      userId,
      infractionType: type,
      severity,
      description,
    });
    setIsInfractionModalOpen(false);
  };

  const handleSendNotification = (user: AdminUser) => {
    setSelectedUser(user);
    setIsPrivateNotificationModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col">
        <div className="flex-1 container mx-auto max-w-6xl p-4 space-y-4 pb-32">
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-64" />
            </CardHeader>
            <CardContent className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="space-y-2">
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-8 w-32" />
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 container mx-auto max-w-6xl p-4 space-y-6 pb-32">
        <AdminDashboard />

        <Card>
          <CardHeader>
            <div className="flex justify-between items-center flex-wrap gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                {t('adminPanel.userManagement')}
                <Badge variant="outline">{users.length} {t('adminPanel.users')}</Badge>
              </CardTitle>
              <Button
                onClick={() => setIsGlobalNotificationModalOpen(true)}
                variant="outline"
                className="bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
              >
                📢 {t('adminPanel.globalNotification')}
              </Button>
            </div>

            <div className="flex gap-4 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
                <Input
                  placeholder={t('adminPanel.searchPlaceholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Button
                variant="outline"
                onClick={() => setSearchTerm('')}
                disabled={!searchTerm}
              >
                {t('adminPanel.clear')}
              </Button>
            </div>
          </CardHeader>

          <CardContent>
            {users.length === 0 ? (
              <div className="text-center py-8">
                <AlertTriangle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">{t('adminPanel.noUsersFound')}</h3>
                <p className="text-muted-foreground">
                  {searchTerm ? t('adminPanel.tryDifferentSearch') : t('adminPanel.noUsersInSystem')}
                </p>
              </div>
            ) : (
              <UserManagementTable
                users={users}
                onEditUser={handleEditUser}
                onDeleteUser={handleDeleteUser}
                onSuspendUser={handleSuspendUser}
                onBanUser={handleBanUser}
                onReactivateUser={handleReactivateUser}
                onAddInfraction={handleAddInfraction}
                onViewHistory={handleViewHistory}
                onViewPosts={handleViewPosts}
                onSendNotification={handleSendNotification}
                isUpdating={isUpdating}
                isDeleting={isDeleting}
                isSuspending={isSuspending}
                isBanning={isBanning}
                isReactivating={isReactivating}
              />
            )}
          </CardContent>
        </Card>

        <UserEditModal
          user={selectedUser}
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveUser}
          isUpdating={isUpdating}
        />

        <UserDeleteConfirmDialog
          user={selectedUser}
          isOpen={isDeleteDialogOpen}
          onClose={() => setIsDeleteDialogOpen(false)}
          onConfirm={handleConfirmDelete}
          isDeleting={isDeleting}
        />

        <SuspendAccountModal
          user={selectedUser}
          isOpen={isSuspendModalOpen}
          onClose={() => setIsSuspendModalOpen(false)}
          onConfirm={handleConfirmSuspend}
          isLoading={isSuspending}
        />

        <BanAccountModal
          user={selectedUser}
          isOpen={isBanModalOpen}
          onClose={() => setIsBanModalOpen(false)}
          onConfirm={handleConfirmBan}
          isLoading={isBanning}
        />

        <InfractionModal
          user={selectedUser}
          isOpen={isInfractionModalOpen}
          onClose={() => setIsInfractionModalOpen(false)}
          onConfirm={handleConfirmInfraction}
          isLoading={isAdding}
        />

        <InfractionHistory
          infractions={infractions}
          userName={selectedUser?.full_name || ''}
          isOpen={isHistoryModalOpen}
          onClose={() => setIsHistoryModalOpen(false)}
          isLoading={isLoadingInfractions}
        />

        <UserPostsViewer
          user={selectedUser}
          isOpen={isPostsViewerOpen}
          onClose={() => setIsPostsViewerOpen(false)}
        />

        <SendPrivateNotificationModal
          open={isPrivateNotificationModalOpen}
          onClose={() => {
            setIsPrivateNotificationModalOpen(false);
            setSelectedUser(null);
          }}
          preselectedUserId={selectedUser?.id}
          preselectedUserName={selectedUser?.full_name}
        />

        <SendGlobalNotificationModal
          open={isGlobalNotificationModalOpen}
          onClose={() => setIsGlobalNotificationModalOpen(false)}
        />

        <BroadcastHistoryTable />
      </div>
      <BottomTabNavigation />
    </div>
  );
};

export default UserManagementPage;
